import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useUser } from '@clerk/react';
import { SessionType } from '@shared';
import { selectSession } from '@client/model/selectors';

const ROLE_OVERRIDE_KEY = 'waterlily-role-override';
const ROLE_OVERRIDE_USER_KEY = 'waterlily-role-override-user';

export function clearRoleOverride() {
  localStorage.removeItem(ROLE_OVERRIDE_KEY);
  localStorage.removeItem(ROLE_OVERRIDE_USER_KEY);
}

export function clearRoleOverrideForDifferentUser(clerkUserId: string) {
  const override = localStorage.getItem(ROLE_OVERRIDE_KEY);
  const owner = localStorage.getItem(ROLE_OVERRIDE_USER_KEY);

  // Overrides created before ownership was tracked are unsafe because they may belong to any
  // previously signed-in account in this browser.
  if (override && owner !== clerkUserId) {
    clearRoleOverride();
  }
}

export function setRoleOverride(targetRole: 'advisor' | 'client', clerkUserId: string) {
  localStorage.setItem(ROLE_OVERRIDE_KEY, targetRole);
  localStorage.setItem(ROLE_OVERRIDE_USER_KEY, clerkUserId);
}

export function useRoleSwitch() {
  const session = useSelector(selectSession);
  const { user } = useUser();

  const currentRole = session.sessionType === SessionType.client ? 'client' : 'advisor';
  const isClientMode = currentRole === 'client';

  const switchRole = useCallback(
    (targetRole: 'advisor' | 'client', destination?: string) => {
      if (targetRole === currentRole && !destination) return;

      if (!user?.id) return;
      setRoleOverride(targetRole, user.id);
      window.location.href = destination ?? (targetRole === 'client' ? '/client/home' : '/advisors');
    },
    [currentRole, user?.id],
  );

  const toggleRole = useCallback(() => {
    switchRole(isClientMode ? 'advisor' : 'client');
  }, [isClientMode, switchRole]);

  return { currentRole, isClientMode, switchRole, toggleRole };
}
